import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { UserGame, getFullUserCollection } from '../lib/game/collectionService';
import { CombinedGameSession, Player } from '../lib/game/sessionService';
import { useGameSessions } from '../features/played-games/hooks/useGameSessions';
import { BarChart, PieChart, Pie, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, Cell, ResponsiveContainer } from 'recharts';
import { Database, LogIn, ArrowLeft, ListFilter, TrendingUp, CheckCircle, XCircle, MinusCircle, Users, CalendarDays, Package, Heart, BarChart3, PieChart as PieChartLucideIcon, BookOpen } from 'lucide-react';
import SEO from '../components/SEO';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';

// Função para truncar rótulos longos
const truncateLabel = (text: string | undefined, maxLength: number = 15): string => {
  if (text && text.length > maxLength) {
    return `${text.substring(0, maxLength - 3)}...`;
  }
  return text || '';
};

// Interface para armazenar os dados de estatísticas
interface MonthlyStat {
  monthYear: string;
  count: number;
}

interface NamedValue {
  name: string;
  value: number;
}

interface OutcomeStat extends NamedValue {
  icon?: React.ElementType;
  color?: string;
}

interface CollectionStats {
  // Gráfico 1: Jogos Jogados vs Jogos na Coleção (REVISADO)
  playedVsNotPlayedInCollection: NamedValue[]; 
  totalGamesInCollection: number;
  // uniqueGamesPlayedCount: number; // Mantido para referência, mas não usado no gráfico principal de pizza

  // Gráficos Temporais (Barras)
  ownedGamesByMonth: MonthlyStat[];
  wishlistGamesByMonth: MonthlyStat[];
  playedSessionsByMonth: MonthlyStat[];

  // Gráficos de Top 5 (Barras)
  topCategoriesInCollection: NamedValue[];
  topMechanicsInCollection: NamedValue[];
  topThemesInCollection: NamedValue[];
  
  // Mantido do anterior
  outcomeStats: OutcomeStat[];
}

// Cores para os gráficos
const COLORS = [
  '#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', 
  '#82ca9d', '#ffc658', '#8dd1e1', '#a4de6c', '#d0ed57',
  '#83a6ed', '#9E67AB', '#555DCD', '#5194FF'
];

// Cores para o gráfico de resultados
const OUTCOME_COLORS: { [key: string]: string } = {
  'Vitórias': '#22C55E', // green-500
  'Derrotas': '#EF4444', // red-500
  'Empates': '#F59E0B',  // amber-500
  'Cooperativos': '#3B82F6', // blue-500
  'Incompletos/Outros': '#6B7280' // gray-500
};

const CollectionStatsPage: React.FC = () => {
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const {
    gameSessions, 
    sessionsLoading, 
    error: sessionsError, 
    sessionsCount,
  } = useGameSessions();

  const [userGamesCollection, setUserGamesCollection] = useState<UserGame[]>([]);
  const [collectionLoading, setCollectionLoading] = useState(true);
  const [collectionError, setCollectionError] = useState<string | null>(null);

  const [stats, setStats] = useState<CollectionStats | null>(null);
  const [pageError, setPageError] = useState<string | null>(null);

  useEffect(() => {
    if (user?.id && isAuthenticated) {
      const fetchCollection = async () => {
        setCollectionLoading(true);
        setCollectionError(null);
        try {
          const collectionData = await getFullUserCollection(user.id);
          setUserGamesCollection(collectionData || []);
        } catch (err) {
          console.error('Erro ao buscar coleção completa:', err);
          setCollectionError('Não foi possível carregar os dados da sua coleção.');
          setUserGamesCollection([]);
        } finally {
          setCollectionLoading(false);
        }
      };
      fetchCollection();
    } else if (!isAuthenticated && !authLoading) {
      // Limpar dados se o usuário deslogar
      setUserGamesCollection([]);
      setCollectionLoading(false);
    }
  }, [user, isAuthenticated, authLoading]);

  const calculateAllStats = useCallback((collection: UserGame[], sessions: CombinedGameSession[], currentUserId: string) => {
    if (!currentUserId) return;
    setPageError(null);
    try {
      const ownedGames = collection.filter(ug => ug.owned);
      const totalGamesInCollection = ownedGames.length;
      
      const playedGameIdsInSessions = new Set<string>();
      sessions.forEach(session => {
        if (session.game_id) playedGameIdsInSessions.add(session.game_id);
      });
      // const uniqueGamesPlayedCount = playedGameIdsInSessions.size; // Jogos únicos com partidas (podem não ser da coleção)

      // Novo cálculo para o gráfico de pizza "Coleção vs Partidas"
      let gamesPlayedFromCollectionCount = 0;
      ownedGames.forEach(ownedGame => {
        if (ownedGame.game_id && playedGameIdsInSessions.has(ownedGame.game_id)) {
          gamesPlayedFromCollectionCount++;
        }
      });
      const gamesNotPlayedFromCollectionCount = totalGamesInCollection - gamesPlayedFromCollectionCount;

      const playedVsNotPlayedInCollection: NamedValue[] = [
        { name: 'Jogados da Coleção', value: gamesPlayedFromCollectionCount },
        { name: 'Não Jogados da Coleção', value: gamesNotPlayedFromCollectionCount },
      ];

      const groupCountByMonthYear = (items: any[], dateField: 'created_at' | 'played_at'): MonthlyStat[] => {
        const counts = new Map<string, number>();
        items.forEach(item => {
          const dateValue = item[dateField];
          if (dateValue) {
            try {
              const date = parseISO(dateValue);
              const monthYear = format(date, 'MM/yyyy', { locale: ptBR });
              counts.set(monthYear, (counts.get(monthYear) || 0) + 1);
            } catch (e) { console.warn(`Data inválida: ${dateValue}`, e); }
          }
        });
        return Array.from(counts.entries())
          .map(([monthYear, count]) => ({ monthYear, count }))
          .sort((a, b) => {
            const [aMonth, aYear] = a.monthYear.split('/');
            const [bMonth, bYear] = b.monthYear.split('/');
            return new Date(+bYear, +bMonth - 1).getTime() - new Date(+aYear, +aMonth - 1).getTime();
          });
      };

      const ownedGamesByMonth = groupCountByMonthYear(collection.filter(ug => ug.owned), 'created_at');
      const wishlistGamesByMonth = groupCountByMonthYear(collection.filter(ug => ug.wishlist), 'created_at');
      const playedSessionsByMonth = groupCountByMonthYear(sessions, 'played_at');

      const countItemsInCollection = (ownedUserGames: UserGame[], field: 'categories' | 'mechanics' | 'themes'): NamedValue[] => {
        const map = new Map<string, number>();
        ownedUserGames.forEach(ug => {
          ug.game?.[field]?.forEach(item => {
            if (item) map.set(item, (map.get(item) || 0) + 1);
          });
        });
        return Array.from(map.entries())
          .map(([name, value]) => ({ name, value }))
          .sort((a, b) => b.value - a.value)
          .slice(0, 5);
      };

      const topCategoriesInCollection = countItemsInCollection(ownedGames, 'categories');
      const topMechanicsInCollection = countItemsInCollection(ownedGames, 'mechanics');
      const topThemesInCollection = countItemsInCollection(ownedGames, 'themes');

      let wins = 0, losses = 0, draws = 0, cooperative = 0, incompleteOrOther = 0;
      sessions.forEach(session => {
        if (session.is_owner) {
          if (session.result === 'vitória') wins++;
          else if (session.result === 'derrota') losses++;
          else if (session.result === 'empate') draws++;
          else if (session.result === 'cooperativo') cooperative++;
          else incompleteOrOther++;
        } else {
          if (session.result === 'cooperativo') cooperative++;
          else if (session.result === 'empate') draws++;
          else if (session.players && session.players.length > 0) {
            const currentUserPlayer = session.players.find(p => p.user_id === currentUserId);
            if (currentUserPlayer) {
              if (currentUserPlayer.is_winner === true) wins++;
              else if (currentUserPlayer.is_winner === false) losses++;
              else incompleteOrOther++;
            } else incompleteOrOther++;
          } else incompleteOrOther++;
        }
      });
      
      const outcomeStats: OutcomeStat[] = [
        { name: 'Vitórias', value: wins, icon: CheckCircle, color: OUTCOME_COLORS['Vitórias'] },
        { name: 'Derrotas', value: losses, icon: XCircle, color: OUTCOME_COLORS['Derrotas'] },
        { name: 'Empates', value: draws, icon: MinusCircle, color: OUTCOME_COLORS['Empates'] },
        { name: 'Cooperativos', value: cooperative, icon: Users, color: OUTCOME_COLORS['Cooperativos'] },
        { name: 'Incompletos/Outros', value: incompleteOrOther, icon: ListFilter, color: OUTCOME_COLORS['Incompletos/Outros'] }
      ].filter(stat => stat.value > 0);

    setStats({
        playedVsNotPlayedInCollection,
        totalGamesInCollection,
        ownedGamesByMonth,
        wishlistGamesByMonth,
        playedSessionsByMonth,
        topCategoriesInCollection,
        topMechanicsInCollection,
        topThemesInCollection,
        outcomeStats
      });
    } catch (error) {
      console.error('Erro ao calcular estatísticas:', error);
      setPageError('Não foi possível processar as estatísticas.');
      setStats(null);
    }
  }, []);

  useEffect(() => {
    if (user?.id && isAuthenticated && !collectionLoading && !sessionsLoading && userGamesCollection && gameSessions) {
      calculateAllStats(userGamesCollection, gameSessions, user.id);
    } else if (!authLoading && !isAuthenticated) {
      setPageError(null);
      setStats(null);
    }
  }, [user, isAuthenticated, authLoading, collectionLoading, sessionsLoading, userGamesCollection, gameSessions, calculateAllStats]);
  
  const overallLoading = authLoading || collectionLoading || sessionsLoading;
  const displayError = collectionError || sessionsError || pageError;

  // Renderização do Loading e Erro inicial
  if (authLoading) { // Modificado para apenas authLoading no bloqueio inicial
    return (
      <div className="container mx-auto max-w-6xl py-8">
        <div className="text-center py-16">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Carregando autenticação...</p>
        </div>
      </div>
    );
  }
  
  if (!isAuthenticated && !authLoading) {
     return (
      <div className="container mx-auto max-w-6xl py-8">
        <div className="text-center py-16 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <LogIn className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500 mb-4" />
          <h3 className="text-xl font-semibold text-gray-700 dark:text-gray-200 mb-2">
            Acesso Necessário
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Faça login para visualizar as estatísticas da sua coleção.
          </p>
          <button
            onClick={() => navigate('/signin')}
            className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2 px-6 rounded-lg inline-flex items-center transition duration-150 ease-in-out"
          >
            Fazer Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEO 
        title="Estatísticas da Coleção e Partidas"
        description="Visualize estatísticas e insights sobre sua coleção de jogos e histórico de partidas."
      />
      <div className="container mx-auto max-w-7xl py-8 px-4 sm:px-6 lg:px-8 text-gray-900 dark:text-gray-100">
        <div className="flex flex-col md:flex-row justify-between items-center mb-8 gap-4">
          <div className="flex items-center gap-3">
            <Link
              to="/collection"
              className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition duration-150 ease-in-out"
            >
              <ArrowLeft className="h-5 w-5" />
            </Link>
            <h1 className="text-3xl font-bold">Estatísticas da Coleção e Partidas</h1>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
            <TrendingUp className="h-5 w-5 text-indigo-500" />
            <span>
              Baseado em {sessionsCount} partidas e {userGamesCollection.filter(ug => ug.owned).length} jogos na coleção.
            </span>
          </div>
        </div>

        {displayError && (
          <div className="bg-red-100 dark:bg-red-900/30 border border-red-400 dark:border-red-700 text-red-700 dark:text-red-300 px-4 py-3 rounded relative mb-6" role="alert">
            <strong className="font-bold">Erro!</strong>
            <span className="block sm:inline"> {displayError}</span>
          </div>
        )}

        {(overallLoading && !stats) && (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500 dark:border-indigo-400 mx-auto"></div>
            <p className="mt-4 text-gray-600 dark:text-gray-400">Carregando estatísticas...</p>
          </div>
        )}

        {!overallLoading && !displayError && !stats && (
          <div className="text-center py-16 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <Database className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500 mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 dark:text-gray-200 mb-2">
              Nenhum dado para exibir
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Parece que não há dados suficientes para gerar estatísticas. Tente adicionar jogos à sua coleção ou registrar partidas.
            </p>
            <div className="flex justify-center space-x-4">
            <button
                onClick={() => navigate('/collection')}
              className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2 px-6 rounded-lg inline-flex items-center transition duration-150 ease-in-out"
            >
                Ver Coleção
              </button>
              <button
                onClick={() => navigate('/collection/played')}
                className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-6 rounded-lg inline-flex items-center transition duration-150 ease-in-out"
              >
                Registrar Partidas
            </button>
            </div>
          </div>
        )}

        {stats && (
          <div className="space-y-8">
            {/* Linha 1: Resultados das Partidas e Visão Geral Coleção vs Jogados */} 
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {stats.outcomeStats.length > 0 && (
                <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md border dark:border-gray-700">
                  <h2 className="text-xl font-semibold mb-4 flex items-center">
                    <ListFilter className="h-5 w-5 mr-2 text-indigo-500" /> Resultados das Partidas
                  </h2>
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={stats.outcomeStats}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          outerRadius={100}
                          fill="#8884d8"
                          dataKey="value"
                          label={({ name, percent, value }) => {
                            const truncatedName = truncateLabel(name, 15);
                            return `${truncatedName}: ${value} (${(percent * 100).toFixed(0)}%)`;
                          }}
                        >
                          {stats.outcomeStats.map((entry, index) => (
                            <Cell key={`cell-outcome-${index}`} fill={entry.color || COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value, name, props: any) => [`${props.payload.value} partidas (${(props.payload.percent * 100).toFixed(1)}%)`, props.payload.name]} />
                        <Legend formatter={(value, entry: any) => {
                          const Icon = entry.payload?.icon;
                          return <span style={{ color: entry.payload?.color || entry.color }}> {Icon && <Icon className="inline h-4 w-4 mr-1" />} {value} </span>;
                        }} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {stats.playedVsNotPlayedInCollection.length > 0 && stats.totalGamesInCollection > 0 && (
                <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md border dark:border-gray-700">
                  <h2 className="text-xl font-semibold mb-4 flex items-center">
                    <PieChartLucideIcon className="h-5 w-5 mr-2 text-green-500" /> % Jogados da Coleção
                  </h2>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                          data={stats.playedVsNotPlayedInCollection}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                          label={({ name, value, percent }) => {
                            const truncatedName = truncateLabel(name, 18);
                            return `${truncatedName}: ${value} (${(percent * 100).toFixed(0)}%)`;
                          }}
                    >
                          {stats.playedVsNotPlayedInCollection.map((entry, index) => (
                            <Cell key={`cell-playedvsnotplayed-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                        <Tooltip formatter={(value, name) => [`${value} jogos`, name]} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
                </div>
              )}
            </div>

            {/* Linha 2: Gráficos Temporais */} 
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {stats.ownedGamesByMonth.length > 0 && (
                 <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md border dark:border-gray-700">
                    <h2 className="text-xl font-semibold mb-4 flex items-center"><BookOpen className="h-5 w-5 mr-2 text-blue-500"/>Jogos Adicionados à Coleção</h2>
                    <div className="h-72">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={stats.ownedGamesByMonth.slice(0, 12).reverse()} margin={{ top: 5, right: 0, left: -25, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(128,128,128,0.2)"/>
                          <XAxis dataKey="monthYear" fontSize={10} />
                          <YAxis allowDecimals={false} fontSize={10}/>
                          <Tooltip formatter={(value: number) => [`${value} jogos`, 'Jogos adicionados']} />
                          <Bar dataKey="count" fill="#3B82F6" name="Jogos Adicionados" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
              )}
              {stats.wishlistGamesByMonth.length > 0 && (
                 <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md border dark:border-gray-700">
                    <h2 className="text-xl font-semibold mb-4 flex items-center"><Heart className="h-5 w-5 mr-2 text-pink-500"/>Jogos Adicionados à Wishlist</h2>
                    <div className="h-72">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={stats.wishlistGamesByMonth.slice(0, 12).reverse()} margin={{ top: 5, right: 0, left: -25, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(128,128,128,0.2)"/>
                          <XAxis dataKey="monthYear" fontSize={10} />
                          <YAxis allowDecimals={false} fontSize={10}/>
                          <Tooltip formatter={(value: number) => [`${value} jogos`, 'Jogos na wishlist']} />
                          <Bar dataKey="count" fill="#EC4899" name="Jogos na Wishlist" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
              )}
              {stats.playedSessionsByMonth.length > 0 && (
                 <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md border dark:border-gray-700">
                    <h2 className="text-xl font-semibold mb-4 flex items-center"><CalendarDays className="h-5 w-5 mr-2 text-teal-500"/>Partidas Registradas</h2>
                    <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={stats.playedSessionsByMonth.slice(0, 12).reverse()} margin={{ top: 5, right: 0, left: -25, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(128,128,128,0.2)"/>
                          <XAxis dataKey="monthYear" fontSize={10} />
                          <YAxis allowDecimals={false} fontSize={10}/>
                          <Tooltip formatter={(value: number) => [`${value} partidas`, 'Partidas registradas']} />
                          <Bar dataKey="count" fill="#14B8A6" name="Partidas Registradas" />
                        </BarChart>
                </ResponsiveContainer>
              </div>
                  </div>
              )}
            </div>

            {/* Linha 3: Top 5s */} 
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {stats.topCategoriesInCollection.length > 0 && (
                 <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md border dark:border-gray-700">
                    <h2 className="text-xl font-semibold mb-4 flex items-center"><BarChart3 className="h-5 w-5 mr-2 text-purple-500"/>Top 5 Categorias (Coleção)</h2>
                    <div className="h-72">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={stats.topCategoriesInCollection} layout="vertical" margin={{ top: 5, right: 20, left: 40, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="rgba(128,128,128,0.2)"/>
                          <XAxis type="number" allowDecimals={false} fontSize={10} />
                          <YAxis type="category" dataKey="name" width={120} fontSize={10} interval={0} tickFormatter={(value) => truncateLabel(value, 18)} />
                          <Tooltip formatter={(value: number, name: string) => [value, name]} />
                          <Bar dataKey="value" fill="#8B5CF6" name="Jogos" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
              )}
              {stats.topMechanicsInCollection.length > 0 && (
                 <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md border dark:border-gray-700">
                    <h2 className="text-xl font-semibold mb-4 flex items-center"><BarChart3 className="h-5 w-5 mr-2 text-sky-500"/>Top 5 Mecânicas (Coleção)</h2>
                    <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={stats.topMechanicsInCollection} layout="vertical" margin={{ top: 5, right: 20, left: 40, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="rgba(128,128,128,0.2)"/>
                          <XAxis type="number" allowDecimals={false} fontSize={10} />
                          <YAxis type="category" dataKey="name" width={120} fontSize={10} interval={0} tickFormatter={(value) => truncateLabel(value, 18)} />
                          <Tooltip formatter={(value: number, name: string) => [value, name]}/>
                          <Bar dataKey="value" fill="#0EA5E9" name="Jogos"/>
                        </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
              )}
              {stats.topThemesInCollection.length > 0 && (
                 <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md border dark:border-gray-700">
                    <h2 className="text-xl font-semibold mb-4 flex items-center"><BarChart3 className="h-5 w-5 mr-2 text-amber-500"/>Top 5 Temas (Coleção)</h2>
                    <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={stats.topThemesInCollection} layout="vertical" margin={{ top: 5, right: 20, left: 40, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="rgba(128,128,128,0.2)"/>
                          <XAxis type="number" allowDecimals={false} fontSize={10}/>
                          <YAxis type="category" dataKey="name" width={120} fontSize={10} interval={0} tickFormatter={(value) => truncateLabel(value, 18)} />
                          <Tooltip formatter={(value: number, name: string) => [value, name]}/>
                          <Bar dataKey="value" fill="#F59E0B" name="Jogos"/>
                        </BarChart>
                </ResponsiveContainer>
              </div>
                  </div>
              )}
            </div>

          </div>
        )}
      </div>
    </>
  );
};

export default CollectionStatsPage; 